
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Datetime } from 'vue-datetime';
import { TimeZone } from "@/store/types";
import { Getter } from 'vuex-class';
export enum ParkType {
    Fixed = "fixed",
    DateTime = "datetime"
}

export interface ParkEventData {
    parkType: ParkType;
    parkHours: number;
    parkMinutes: number;
    parkUntilDateTime: string;
}

@Component({
    components: {
        'datetime': Datetime
    }
})
export default class ParkEventOptions extends Vue {

    @Prop({ type: Number, default: 0 }) private initialHours!: number;
    @Prop({ type: Number, default: 10 }) private initialMinutes!: number;
    @Prop({ type: Object, default: null }) private areaTimeZone!: TimeZone | null;
	@Getter getFeature: (featuresList: string[]) => boolean;

    private parkType: ParkType = ParkType.Fixed;
    private parkHours: number = 0;
    private parkMinutes: number = 0;
    private parkUntilDateTime: string = "";

    private readonly ParkType = ParkType;

    private mounted(): void {
        this.parkHours = this.initialHours;
        this.parkMinutes = this.initialMinutes;
    }

    @Watch("parkType")
    private onParkTypeChanged(newValue: ParkType): void {
        if (newValue === ParkType.DateTime) {
            const currentTime = Date.now();
            if (!this.parkUntilDateTime) {

                // Calculate the offset in milliseconds for the initial hours.
                // 1 hour = 60 minutes, 1 minute = 60,000 ms, so multiply hours by 60 * 60,000.
                const initialHoursOffset = this.initialHours * 60 * 60000;

                // Calculate the offset in milliseconds for the initial minutes.
                // 1 minute = 60,000 ms, so multiply minutes by 60,000.
                const initialMinutesOffset = this.initialMinutes * 60000;

                const initialTime = currentTime + initialHoursOffset + initialMinutesOffset;
                
                this.parkUntilDateTime = new Date(initialTime).toISOString();
            }

            const date = new Date(this.parkUntilDateTime);
            this.$emit('park-date-time-valid', (date.getTime() > currentTime));

        } else {
            this.$emit('park-date-time-valid', true); // Reset validation state when park type change
        }
        this.emitState();
    }

    @Watch("parkHours")
    private onParkHoursChanged(): void {
        this.emitState();
    }

    @Watch("parkMinutes")
    private onParkMinutesChanged(): void {
        this.emitState();
    }

    private get isParkUntilDateTimeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Park", "ParkUntilDateTime"]);
	}

    private get isParkingAnEventOptional(): boolean {
		return this.getFeature(["Alarms", "EventQueue", "OptionalParkOnLeave"]);
	}

    @Watch("parkUntilDateTime")
    private onParkUntilDateTimeChanged(value: string): void {
        const currentTime = Date.now();
        if (value && new Date(value).getTime() < currentTime) {
            this.$emit('park-date-time-valid', false);

            return;
        } 

        this.$emit('park-date-time-valid', true);

        if (value) {
            const diffMinutes = Math.floor((new Date(value).getTime() - currentTime) / 60000);
            const clamped = Math.max(0, diffMinutes);
            this.parkHours = Math.floor(clamped / 60);
            this.parkMinutes = clamped % 60;
        }
        this.emitState();
    }

    private validateHours(): void {
        if (this.parkHours < 0) {
            this.parkHours = 0;
        } else if (this.parkHours >= 24) {
            this.parkHours = 24;
            this.parkMinutes = 0;
        }
    }

    private validateMinutes(): void {
        if (this.parkHours >= 24) {
            this.parkMinutes = 0;
        } else if (this.parkMinutes < 0) {
            this.parkMinutes = 0;
        } else if (this.parkMinutes > 59) {
            this.parkMinutes = 59;
        }
    }

    private getDateString(calculateTimezone: boolean) {
        const  dateStr = new Date(this.parkUntilDateTime).toLocaleDateString("en-US", { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                timeZone: calculateTimezone && this.areaTimeZone ? this.areaTimeZone.iana : undefined
            }
        );

        const timeStr = new Date(this.parkUntilDateTime).toLocaleTimeString("en-US", {
            hour12: true,
            hour: "numeric",
            minute: "numeric",
            timeZone: calculateTimezone && this.areaTimeZone ? this.areaTimeZone.iana : undefined
        });
        return `${dateStr} ${timeStr}`;
    }

    private get localDateTimeDisplay(): string {
        if (!this.parkUntilDateTime) return "";
        return this.getDateString(false);
    }

    private get areaDateTimeDisplay(): string {
        if (!this.parkUntilDateTime || !this.areaTimeZone?.iana) return "";
        return this.getDateString(true);
    }

    private emitState(): void {
        this.$emit('park-options-changed', {
            parkType: this.parkType,
            parkHours: this.parkHours,
            parkMinutes: this.parkMinutes,
            parkUntilDateTime: this.parkUntilDateTime
        });
    }
}
